import { Button, Carousel, Divider } from '@design-system';
import classNames from 'classnames';
import { useState } from 'react';

import { pathFor } from '../../helpers/links';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { renderStoryblokRichText } from '../../utils/renderStoryblokRichText';
import { Image } from '../Image';
import PageHeading from '../Layout/PageHeading';
import PageSection from '../Layout/PageSection';

type Props = {
  content: AffiliatesPageType;
};

const Ambassadors: React.FC<Props> = ({ content }) => {
  const [emitter] = useAnalyticsEmitter();
  const [selectedGalleryGroup, setSelectedGalleryGroup] = useState(
    content.galleryImageGroups[0],
  );

  return (
    <main className="md:space-y-15 my-9 space-y-12 md:my-12">
      <PageSection>
        <PageHeading
          align="center"
          heading={content.heading}
          strapline={content.subheading}
        />
      </PageSection>

      <PageSection fullWidth>
        <div className="flex w-full justify-center space-x-3 overflow-hidden">
          {content.introImages.map((image) => (
            <div
              key={image.id}
              className="w-full max-w-3xl flex-shrink-0 overflow-hidden"
            >
              <Image
                alt={image.alt}
                className="overflow-hidden rounded-sm"
                eagerLoading
                focus={image.focus}
                size={{
                  sm: { ratio: '4/3', span: 12 },
                  md: { ratio: '4/3', span: 8 },
                  lg: { ratio: '4/3', span: 6 },
                }}
                src={image.filename}
              />
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection>
        <div className="mx-auto w-full max-w-lg">
          {renderStoryblokRichText(content.introCopy)}
          <div className="flex flex-col items-center pt-3">
            <Button
              as="a"
              href={content.applyLink.url}
              label={content.applyCopy}
              onClick={() => {
                emitter('apply now', 'clicked');
              }}
              target="_blank"
              variant="primary"
            />
          </div>
        </div>
      </PageSection>

      <div className="wrapper-website">
        <Divider className="my-9 md:my-12" />
      </div>

      <PageSection>
        <div className="mx-auto w-full max-w-md">
          <div className="flex flex-col space-y-3 text-center">
            <h2 className="text-md font-bold md:text-lg">
              {content.galleryHeading}
            </h2>
            <p>{content.galleryCopy}</p>
          </div>
        </div>
      </PageSection>

      <PageSection fullWidth>
        <div className="flex flex-col space-y-9">
          <div
            className="relative flex overflow-x-scroll px-[var(--padding)] py-1 no-scrollbar md:justify-center md:overflow-x-visible"
            style={
              {
                '--padding':
                  'calc(50% - (var(--website-max-width) / 2) + var(--website-paddings))',
              } as React.CSSProperties
            }
          >
            {content.galleryImageGroups.map((group) => (
              <Button
                key={`selector-${group._uid}`}
                className={classNames('shrink-0 !rounded-full')}
                label={group.name}
                onClick={() => {
                  setSelectedGalleryGroup(group);
                  emitter('category', 'selected', {
                    category: group.name,
                  });
                }}
                size="md"
                variant={
                  group._uid === selectedGalleryGroup._uid
                    ? 'secondary'
                    : 'transparent'
                }
              />
            ))}
          </div>
          <div className="relative">
            <div className="block md:hidden">
              <Carousel
                arrowPosition="middle"
                items={selectedGalleryGroup.images.map((image) => (
                  <Image
                    key={image.id}
                    alt={image.alt}
                    className="overflow-hidden rounded-sm"
                    focus={image.focus}
                    size={{ sm: { ratio: '3/4', span: 12 } }}
                    src={image.filename}
                  />
                ))}
                perPage={2}
              />
            </div>
            <div className="relative hidden md:block">
              {content.galleryImageGroups.map((group) => (
                <div key={group._uid} className="relative overflow-hidden">
                  <div
                    className={classNames(
                      'flex w-full justify-center gap-x-3 overflow-hidden',
                      selectedGalleryGroup._uid === group._uid
                        ? 'relative'
                        : 'absolute',
                    )}
                  >
                    {group.images.map((image) => (
                      <div
                        key={image.id}
                        className={classNames(
                          'w-120 flex-shrink-0 overflow-hidden',
                          'transition-opacity duration-500',
                          selectedGalleryGroup._uid === group._uid
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      >
                        <Image
                          alt={image.alt}
                          className="overflow-hidden rounded-sm"
                          focus={image.focus}
                          size={{
                            sm: { ratio: '3/4', span: 12 },
                            md: { ratio: '3/4', span: 3 },
                            lg: { ratio: '3/4', span: 3 },
                          }}
                          src={image.filename}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageSection>

      <div className="wrapper-website">
        <Divider className="my-9 md:my-12" />
      </div>

      <PageSection>
        <div className="mx-auto mb-9 w-full max-w-md md:mb-12">
          <div className="flex flex-col space-y-3 text-center">
            <h2 className="text-md font-bold md:text-lg">
              {content.stepsHeading}
            </h2>
          </div>
        </div>
        <div className="grid-section gap-y-5">
          {content.steps.map((step) => (
            <div
              key={step._uid}
              className="col-span-12 min-h-72 space-y-3 rounded-sm border border-divider/20 p-6 md:col-span-6 lg:col-span-3"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-active-4 text-center text-white">
                {step.number}
              </span>
              <h3 className="text-md font-bold md:text-lg">{step.heading}</h3>
              <p className="text-neutral-4">{step.copy}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <div className="wrapper-website">
        <Divider className="my-9 md:my-12" />
      </div>

      <PageSection>
        <div className="mx-auto flex max-w-md flex-col space-y-3 text-center">
          <h2 className="text-md font-bold md:text-lg">
            {content.artistsHeading}
          </h2>
          <p>{content.artistsCopy}</p>
        </div>
      </PageSection>

      <PageSection fullWidth>
        <div className="block w-full md:hidden">
          <Carousel
            arrowPosition="middle"
            items={pairs(content.artists).map((pair, idx) => (
              <div key={idx} className="flex flex-row space-x-5">
                {pair.map((artist) => (
                  <div className="flex w-full flex-col space-y-3">
                    <Image
                      alt={artist.content.thumbnail.alt}
                      className="overflow-hidden rounded-full"
                      focus={artist.content.thumbnail.focus}
                      src={artist.content.thumbnail.filename}
                      width={128}
                    />
                    <span className="block text-center text-neutral-4">
                      {artist.name}
                    </span>
                  </div>
                ))}
              </div>
            ))}
            perPage={2}
          />
        </div>
        <div className="hidden w-full justify-center gap-x-5 overflow-hidden md:flex">
          {content.artists.map((artist) => (
            <div key={artist._uid} className="flex-shrink-0 md:w-60">
              <div className="flex w-full flex-col space-y-3">
                <Image
                  alt={artist.content.thumbnail.alt}
                  className="overflow-hidden rounded-full"
                  focus={artist.content.thumbnail.focus}
                  size={{
                    sm: { ratio: '1/1', span: 6 },
                    md: { ratio: '1/1', span: 2 },
                    lg: { ratio: '1/1', span: 2 },
                  }}
                  src={artist.content.thumbnail.filename}
                />
                <span className="block text-center text-neutral-4">
                  {artist.name}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex w-full flex-row justify-center">
          <Button
            as="a"
            href={pathFor({ page: 'ARTISTS_INDEX' })}
            label="See all artists"
            onClick={() => {
              emitter('see artists', 'clicked');
            }}
            target="_blank"
            variant="tertiary"
          />
        </div>
      </PageSection>

      <div className="wrapper-website">
        <Divider className="my-9 md:my-12" />
      </div>

      <PageSection>
        <div className="mx-auto mb-9 w-full max-w-md md:mb-12">
          <div className="flex flex-col space-y-3 text-center">
            <h2 className="text-md font-bold md:text-lg">
              {content.programmeHeading}
            </h2>
          </div>
        </div>
        <div className="mx-auto max-w-xl">
          <div className="grid-section gap-y-5">
            {content.programmeBenefits.map((benefit) => (
              <div
                key={benefit._uid}
                className="col-span-6 flex h-40 items-center justify-center rounded-sm border border-divider/20 p-4 text-center"
              >
                {benefit.copy}
              </div>
            ))}
          </div>
        </div>
      </PageSection>

      <div className="wrapper-website">
        <Divider className="my-9 md:my-12" />
      </div>

      <PageSection>
        <div className="overflow-hidden rounded-md">
          <div
            className="relative"
            style={{
              background: `url(${content.ctaBackground.filename}) no-repeat`,
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
            }}
          >
            <div className="absolute left-0 top-0 h-full w-full bg-neutral-6 opacity-40" />
            <div className="grid-section relative px-4">
              <div className="col-span-12 m-auto max-w-lg py-24 text-center md:col-span-6 md:col-start-4">
                <div className="pb-3">
                  <h2 className="text-md font-bold text-white md:text-lg">
                    {content.ctaHeading}
                  </h2>
                </div>
                <p className="text-white">{content.ctaCopy}</p>
                <div className="m-auto mt-6 w-44">
                  <Button
                    as="a"
                    href={content.ctaButtonLink.url}
                    label={content.ctaButtonCopy}
                    onClick={() => {
                      emitter('send application', 'clicked');
                    }}
                    target="_blank"
                    variant="primary"
                  />
                </div>
              </div>
            </div>
            <div className="relative bg-neutral-6 py-6 text-center text-white">
              {renderStoryblokRichText(content.ctaExtra, {
                paragraphStyle: 'tight',
              })}
            </div>
          </div>
        </div>
      </PageSection>
    </main>
  );
};

export default Ambassadors;

function pairs<T>(arr: T[]): T[][] {
  const result: T[][] = [];

  for (let i = 0; i < arr.length; i++) {
    if (i % 2 === 0) {
      result.push([arr[i]]);
    } else {
      result[result.length - 1].push(arr[i]);
    }
  }

  return result;
}
